<template>
  <base-drawer :show="show" :title="drawerTitle" @close="onClose">
    <div class="mb-3">
      <segmented-toggle variant="pills" size="sm" :value="mode" :options="modeOptions" @input="onModeChange" />
      <info-hint variant="neutral" style="width: 100%" class="mb-0">{{ modeHint }}</info-hint>
    </div>

    <!-- New location -->
    <drawer-section v-if="mode === 'new'" title="New location" icon="icon-square-pin">
      <label class="text-white">Name</label>
      <base-input
        type="text"
        placeholder="Location name"
        maxlength="50"
        v-model="name"
        :error="errors.name"
        @input="touched.name = true"
      />
      <label class="text-white">Code</label>
      <base-input
        type="text"
        placeholder="Code"
        maxlength="25"
        v-model="code"
        :error="errors.code"
        @input="touched.code = true"
      />
      <info-hint variant="neutral" style="width: 100%">
        The code must be unique across every location and cannot contain spaces.
      </info-hint>
    </drawer-section>

    <!-- Target location (existing and move) -->
    <drawer-section v-if="mode !== 'new'" title="Target location" icon="icon-square-pin">
      <base-input class="mb-0">
        <el-select
          :value="targetGroupId"
          class="select-primary"
          filterable
          placeholder="Choose a location"
          @input="onTargetChange"
        >
          <el-option v-for="g in targetOptions" :key="g.id" class="select-primary" :label="g.name" :value="g.id" />
        </el-select>
      </base-input>
    </drawer-section>

    <!-- Areas (new and existing) -->
    <drawer-section v-if="mode !== 'move'" title="What to copy" icon="icon-single-copy-04">
      <div class="copy-group-rows">
        <label class="copy-group-row copy-group-row--all">
          <input
            type="checkbox"
            :checked="allSelected"
            :indeterminate.prop="isIndeterminate"
            @change="onToggleAll"
          />
          <span class="copy-group-name">Select all</span>
        </label>
        <label
          v-for="area in AREAS"
          :key="area.key"
          class="copy-group-row"
        >
          <input
            type="checkbox"
            :value="area.key"
            v-model="selectedAreas"
            @change="resetPreview"
          />
          <span class="copy-group-name">{{ area.label }}</span>
          <span
            v-if="mode === 'copy_existing' && area.replace"
            class="tag-chip text-white"
            :style="tagChipStyle('#fd5d93')"
          >Replaces target settings</span>
        </label>
      </div>

      <info-hint v-if="mode === 'new'" variant="primary" style="width: 100%" class="mt-3">
        The copy starts with a clean history: no bookings, transactions, customers, waivers, photos,
        sold gift cards or passes, and no sent emails or texts. It also starts with its own
        connections: set up the payment gateway, sending domain, SMS, point of sale devices,
        integrations and analytics on the new location. Copied schedules are inactive until you
        turn them on, and only you can see the new location until you add staff to it.
      </info-hint>
      <info-hint v-else variant="primary" style="width: 100%" class="mt-3">
        Events, templates, items, dashboards and other lists are added next to what the target
        already has. Records with the same name on both locations can reuse the target's record
        instead; the preview lets you choose. Areas marked as replacing overwrite the target's
        current settings. Bookings, transactions, customers and connections are never copied.
      </info-hint>

      <div v-if="mode === 'copy_existing'" class="d-flex justify-content-end mt-3">
        <base-button type="primary" size="sm" :disabled="!canPreview || previewLoading" @click="onPreview">
          {{ previewLoading ? 'Loading preview...' : 'Preview' }}
        </base-button>
      </div>
    </drawer-section>

    <!-- Families (merge) -->
    <drawer-section v-if="mode === 'move'" title="What moves" icon="icon-components">
      <div class="copy-group-rows">
        <label class="copy-group-row copy-group-row--all">
          <input type="checkbox" :checked="true" disabled />
          <span class="copy-group-name">Events, bookings, orders and payments, event records, booking requests, gift cards sold, waivers, and every setting that is not a connection <small class="text-muted">(always)</small></span>
        </label>
        <label v-for="f in FAMILIES" :key="f.key" class="copy-group-row">
          <input type="checkbox" :value="f.key" v-model="selectedFamilies" @change="resetPreview" />
          <span class="copy-group-name">{{ f.label }}</span>
        </label>
      </div>
      <info-hint variant="warning" icon="icon-alert-circle-exc" style="width: 100%" class="mt-3">
        The whole location merges into the target and is archived when the merge finishes. Anything
        you leave unticked stays on the archived location; unarchive it to see it again. The payment
        gateway, point of sale devices, SMS number, sending domain, integrations and analytics ids
        never move.
      </info-hint>
      <div class="d-flex justify-content-end mt-3">
        <base-button type="primary" size="sm" :disabled="!canPreview || previewLoading" @click="onPreview">
          {{ previewLoading ? 'Loading preview...' : 'Preview' }}
        </base-button>
      </div>
    </drawer-section>

    <!-- Preview -->
    <drawer-section v-if="mode !== 'new' && preview" title="What will happen" icon="icon-zoom-split">
      <ul class="pl-3 mb-2">
        <li v-for="(line, i) in previewLines" :key="i" class="text-white">{{ line }}</li>
      </ul>

      <info-hint v-for="(w, i) in (preview.warnings || [])" :key="'w' + i" variant="warning" icon="icon-alert-circle-exc" style="width: 100%">
        {{ w }}
      </info-hint>
      <info-hint v-for="(b, i) in (preview.blocking || [])" :key="'b' + i" variant="danger" icon="icon-alert-circle-exc" style="width: 100%">
        {{ b }}
      </info-hint>

      <!-- Match table: only where the target has a row that could be reused -->
      <template v-for="m in matchablesToShow">
        <h5 :key="m.map + '-label'" class="text-white mt-4 mb-2">{{ m.label }} <small class="text-muted">{{ m.source.length }}</small></h5>
        <div :key="m.map + '-rows'" class="copy-group-rows">
          <div v-for="s in m.source" :key="s.id" class="copy-group-row copy-group-row--match">
            <span class="copy-group-name">{{ s.name }}</span>
            <base-input v-if="m.targets.length > 0" class="mb-0 copy-group-match-select">
              <el-select
                size="mini"
                class="select-primary"
                :value="matchValue(m.map, s.id)"
                @input="(v) => setMatch(m.map, s.id, v)"
              >
                <el-option class="select-primary" :label="unmatchedLabel" :value="0" />
                <el-option v-for="t in m.targets" :key="t.id" class="select-primary" :label="`Use existing: ${t.name}`" :value="t.id" />
              </el-select>
            </base-input>
            <small v-else class="text-muted">{{ unmatchedLabel }}</small>
          </div>
        </div>
      </template>
    </drawer-section>

    <!-- Confirmation -->
    <drawer-section v-if="needsConfirmation" title="Confirm" icon="icon-lock-circle">
      <div class="copy-group-rows">
        <label v-for="(ack, i) in acknowledgments" :key="i" class="copy-group-row copy-group-row--ack">
          <input type="checkbox" v-model="acks[i]" />
          <span class="copy-group-name">{{ ack }}</span>
        </label>
      </div>
      <label class="text-white mt-3">Type the target location name to confirm</label>
      <base-input type="text" :placeholder="targetName" v-model="confirmName" />
    </drawer-section>

    <template #footer>
      <div class="footer-actions">
        <base-button type="secondary" size="sm" @click="onClose">Cancel</base-button>
        <base-button
          type="primary"
          size="sm"
          :disabled="!canSubmit"
          @click="onSubmit"
        >
          {{ submitLabel }}
        </base-button>
      </div>
    </template>
  </base-drawer>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { tagChipStyle } from '@/util/tagChip'

interface Area {
  key: string
  label: string
  replace?: boolean
}

interface GroupRow {
  id: number
  name: string
  archived?: number
  copy_status?: string | null
  games?: string | Array<{ id: number; name: string }> | null
}

interface Matchable {
  map: string
  table: string
  label: string
  source: Array<{ id: number; name: string }>
  targets: Array<{ id: number; name: string }>
  proposed: Record<string, number | null>
}

interface Preview {
  counts?: Record<string, number | string>
  replaces?: Array<{ table: string; area: string }>
  skipped?: Record<string, number>
  matchables?: Matchable[]
  familyCounts?: Record<string, Array<{ label: string; n: number }>>
  collections?: Record<string, number>
  warnings?: string[]
  blocking?: string[]
}

type Mode = 'new' | 'copy_existing' | 'move'

// Same keys and order as the copy-company-group lambda's AREA_KEYS. `replace` marks the areas
// that overwrite one-row-per-group settings when copying into an existing location.
const AREAS: Area[] = [
  { key: 'events', label: 'Events' },
  { key: 'checklists', label: 'Checklists' },
  { key: 'schedules', label: 'Schedules (copied inactive)' },
  { key: 'custom_fields', label: 'Custom fields and tags' },
  { key: 'booking_site', label: 'Booking site templates' },
  // request_forms is supported by the copy lambdas; add the row back when request forms ship (OTC-482).
  { key: 'payments', label: 'Payment settings, fees, taxes and payment pages', replace: true },
  { key: 'products', label: 'Items, gift card types and pass types' },
  { key: 'promo_codes', label: 'Promo codes and promo code groups' },
  { key: 'waivers', label: 'Waiver template', replace: true },
  { key: 'surveys', label: 'Surveys' },
  { key: 'messaging', label: 'Email and SMS', replace: true },
  { key: 'booking_settings', label: 'Booking statuses and calendar settings', replace: true },
  { key: 'venue_settings', label: 'Tracker, photo, staff and time log settings', replace: true },
  { key: 'dashboards_reports', label: 'Dashboards and reports' },
]

const TABLE_LABELS: Record<string, [string, string]> = {
  games: ['event', 'events'], checklists: ['checklist', 'checklists'], game_schedules: ['schedule', 'schedules'],
  custom_fields: ['custom field', 'custom fields'], tags: ['tag', 'tags'],
  booking_site_settings: ['booking site template', 'booking site templates'], request_forms: ['request form', 'request forms'],
  fees: ['fee', 'fees'], taxes: ['tax', 'taxes'], custom_payment_methods: ['payment method', 'payment methods'],
  payment_pages: ['payment page', 'payment pages'], items: ['item', 'items'], gift_card_types: ['gift card type', 'gift card types'],
  pass_types: ['pass type', 'pass types'], promo_code_templates: ['promo code group', 'promo code groups'],
  promo_codes: ['promo code', 'promo codes'], surveys: ['survey', 'surveys'], email_templates: ['email template', 'email templates'],
  sms_templates: ['SMS template', 'SMS templates'], email_automation_rules: ['automation (added inactive)', 'automations (added inactive)'],
  workflow_states: ['workflow state', 'workflow states'], booking_slot_status_types: ['booking status', 'booking statuses'],
  time_log_categories: ['time log category', 'time log categories'], dashboards: ['dashboard', 'dashboards'], reports: ['report', 'reports'],
}
function tableLabel(table: string, n: number): string {
  const pair = TABLE_LABELS[table]
  if (!pair) return table.replace(/_/g, ' ')
  return Number(n) === 1 ? pair[0] : pair[1]
}

const REPLACE_LABELS: Record<string, string> = {
  payment_settings: 'payment settings', gift_card_settings: 'gift card page settings', waiver_configurations: 'waiver template',
  automated_email_settings: 'automated email sender name and reply-to, and each automated email with the same type',
  calendar_policy_settings: 'calendar settings', game_tracker_settings: 'tracker settings', photo_settings: 'photo settings',
  staff_management_settings: 'staff settings',
}

const MOVE_ACKS = [
  'The source location is archived when the merge finishes. Anything left unticked stays on it; unarchive it to see it again.',
  'Bookings, orders, photos and history leave the source location\'s reports and appear on the target\'s.',
  'Payments taken before the merge were charged through the source location\'s payment gateway. Refunds after the merge go through the target\'s gateway and succeed only while both locations use the same account.',
  'The merged events are removed from the source booking site and are not added to the target booking site. Pending reminders send from the target location\'s email and SMS settings.',
]
const REPLACE_ACK = 'This replaces the target location\'s current settings for the ticked areas. The previous values are not kept.'

interface Family { key: string; label: string; stays: string }
// Same keys as FAMILY_KEYS in copy-company-group and the worker. `stays` is the preview line
// when the family is left unticked.
const FAMILIES: Family[] = [
  { key: 'photos', label: 'Photos', stays: 'Photos stay on the archived location; they still point at event records that moved.' },
  { key: 'passes_credits', label: 'Passes and customer credits', stays: 'Passes and credits stay on the archived location and cannot be used on the target.' },
  { key: 'customers', label: 'Customers (link them to the target, with tags, consents and notes)', stays: 'Customers are not linked to the target; their orders and waivers still move.' },
  { key: 'projects', label: 'Projects and work tickets', stays: 'Projects and work tickets stay on the archived location.' },
  { key: 'time_logs', label: 'Time logs', stays: 'Time logs stay on the archived location.' },
  { key: 'messaging_history', label: 'Email and SMS history, campaigns and audiences', stays: 'Sent emails, texts, campaigns and audiences stay on the archived location.' },
  { key: 'resources', label: 'Resources and their assignments', stays: 'Resources stay behind; future bookings lose their assignments and autoblocking re-runs on the target.' },
  { key: 'schedules', label: 'Schedules', stays: 'Schedules stay on the archived location; the moved events are unlinked from them.' },
  { key: 'carts', label: 'Carts and abandoned carts', stays: 'Carts stay on the archived location.' },
]

const props = defineProps<{
  show: boolean
  group?: GroupRow | null
  groups?: GroupRow[]
  loading?: boolean
  preview?: Preview | null
  previewLoading?: boolean
  canMove?: boolean
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'copy', payload: { name: string; code: string; areas: Record<string, boolean> }): void
  (e: 'preview', payload: Record<string, unknown>): void
  (e: 'run', payload: Record<string, unknown>): void
  (e: 'preview-reset'): void
}>()

const mode = ref<Mode>('new')
const name = ref('')
const code = ref('')
const selectedAreas = ref<string[]>([])
const targetGroupId = ref<number | null>(null)
const selectedFamilies = ref<string[]>(FAMILIES.map((f) => f.key))
const matches = ref<Record<string, Record<string, number | null>>>({})
const acks = ref<boolean[]>([])
const confirmName = ref('')
const errors = ref<{ name: string; code: string }>({ name: '', code: '' })
const touched = ref<{ name: boolean; code: boolean }>({ name: false, code: false })

const modeOptions = computed(() => {
  const opts = [
    { value: 'new', label: 'Copy to new location' },
    { value: 'copy_existing', label: 'Copy to existing location' },
  ]
  if (props.canMove) opts.push({ value: 'move', label: 'Merge' })
  return opts
})

const MODE_HINTS: Record<Mode, string> = {
  new: 'Creates a new location from this one\'s settings and events. Nothing live comes with it: no bookings, orders, customers or connections.',
  copy_existing: 'Adds this location\'s settings and events to a location you already have. Lists are added next to what is there; replace areas overwrite that location\'s settings.',
  move: 'Merges this whole location into another one: events, bookings, orders, history and settings move with their ids, and this location is archived when the merge finishes.',
}
const modeHint = computed(() => MODE_HINTS[mode.value])

// Same rules as the group settings page (GroupSettings.vue validFields) and the
// copy-company-group lambda.
function validateName(): boolean {
  const v = name.value.trim()
  if (v.length === 0) errors.value.name = 'Name is required'
  else if (v.length > 50) errors.value.name = 'Name cannot be more than 50 characters'
  else errors.value.name = ''
  return errors.value.name === ''
}

function validateCode(): boolean {
  const v = code.value.trim()
  if (v.length === 0) errors.value.code = 'Code is required'
  else if (/\s/.test(code.value)) errors.value.code = 'Code cannot contain spaces'
  else if (v.length > 25) errors.value.code = 'Code cannot be more than 25 characters'
  else errors.value.code = ''
  return errors.value.code === ''
}

const sourceName = computed(() => (props.group && props.group.name) || '')
const drawerTitle = computed(() => {
  if (mode.value === 'move') return sourceName.value ? `Merge ${sourceName.value} into another location` : 'Merge location'
  return sourceName.value ? `Copy ${sourceName.value}` : 'Copy or merge'
})

const targetOptions = computed(() => {
  const sourceId = props.group && props.group.id
  return (props.groups || []).filter((g) =>
    g.id !== sourceId && Number(g.archived || 0) === 0 && (!g.copy_status || g.copy_status === 'ready'))
})
const targetName = computed(() => {
  const t = targetOptions.value.find((g) => g.id === targetGroupId.value)
  return t ? t.name : ''
})

const allSelected = computed(() => selectedAreas.value.length === AREAS.length)
const isIndeterminate = computed(() => selectedAreas.value.length > 0 && !allSelected.value)
const replaceTicked = computed(() => AREAS.some((a) => a.replace && selectedAreas.value.includes(a.key)))

const canPreview = computed(() => {
  if (!targetGroupId.value) return false
  if (mode.value === 'copy_existing') return selectedAreas.value.length > 0
  if (mode.value === 'move') return true
  return false
})

const hasBlocking = computed(() => !!(props.preview && props.preview.blocking && props.preview.blocking.length > 0))

const needsConfirmation = computed(() => {
  if (!props.preview || hasBlocking.value) return false
  if (mode.value === 'move') return true
  return mode.value === 'copy_existing' && replaceTicked.value
})

const acknowledgments = computed(() => (mode.value === 'move' ? MOVE_ACKS : [REPLACE_ACK]))

// What an unmatched row becomes: a copy when copying, its own moved record when merging.
const unmatchedLabel = computed(() => (mode.value === 'move' ? 'Move as its own record' : 'Create copy'))

const matchablesToShow = computed<Matchable[]>(() =>
  ((props.preview && props.preview.matchables) || []).filter((m) => m.source.length > 0 && m.targets.length > 0))

// Matchable tables where the target has nothing to reuse collapse to one preview line.
const createOnlyLines = computed<string[]>(() =>
  ((props.preview && props.preview.matchables) || [])
    .filter((m) => m.source.length > 0 && m.targets.length === 0)
    .map((m) => {
      const n = m.source.length
      const label = tableLabel(m.table, n) === m.table.replace(/_/g, ' ') ? m.label.toLowerCase() : tableLabel(m.table, n)
      return mode.value === 'move'
        ? `Moves ${n} ${label} as ${n === 1 ? 'it is' : 'they are'} (none exist on the target to reuse)`
        : `Creates ${n} ${label} on the target (none exist there to reuse)`
    }))

function formatCount(n: number | string, singular: string, plural?: string): string {
  const v = Number(n)
  return `${v.toLocaleString()} ${v === 1 ? singular : (plural || singular)}`
}

const previewLines = computed<string[]>(() => {
  const p = props.preview
  if (!p) return []
  const lines: string[] = []
  if (mode.value === 'copy_existing') {
    for (const [table, n] of Object.entries(p.counts || {})) {
      if (Number(n) > 0) lines.push(`Adds ${Number(n).toLocaleString()} ${tableLabel(table, Number(n))}`)
    }
    for (const r of p.replaces || []) lines.push(`Replaces the target's ${REPLACE_LABELS[r.table] || r.table.replace(/_/g, ' ')}`)
    for (const [table, n] of Object.entries(p.skipped || {})) {
      if (Number(n) > 0) lines.push(`Skips ${Number(n).toLocaleString()} ${tableLabel(table, Number(n))} that already ${Number(n) === 1 ? 'exists' : 'exist'} on the target`)
    }
    if (lines.length === 0) lines.push('Nothing to copy for the ticked areas')
  } else if (p.counts) {
    const c = p.counts
    const archivedNote = Number(c.archived_events || 0) > 0 ? ` (plus ${formatCount(c.archived_events, 'archived event', 'archived events')} with their history, still archived on the target)` : ''
    lines.push(`Moves ${formatCount(c.events || 0, 'event', 'events')}${archivedNote} with ${formatCount(c.bookings || 0, 'booking', 'bookings')}, ${formatCount(c.orders || 0, 'order', 'orders')}, ${formatCount(c.instances || 0, 'event record', 'event records')}, ${formatCount(c.waivers || 0, 'waiver', 'waivers')}, ${formatCount(c.booking_requests || 0, 'booking request', 'booking requests')} and ${formatCount(c.gift_cards || 0, 'gift card sold', 'gift cards sold')}`)
    const settings = Object.values(p.collections || {}).reduce((a, n) => a + Number(n || 0), 0)
    if (settings > 0) lines.push(`Moves ${settings.toLocaleString()} settings records (templates, surveys, dashboards, reports, checklists, promo groups and more); booking site templates and automations arrive switched off`)
    for (const f of FAMILIES) {
      const parts = (p.familyCounts && p.familyCounts[f.key]) || []
      const total = parts.reduce((a, x) => a + Number(x.n || 0), 0)
      if (selectedFamilies.value.includes(f.key)) {
        if (total > 0) lines.push(`Moves ${parts.filter((x) => Number(x.n) > 0).map((x) => formatCount(x.n, x.label, x.label)).join(', ')}`)
        if (f.key === 'customers' && Number(c.customers_to_link || 0) > 0) lines.push(`Links ${formatCount(c.customers_to_link, 'customer', 'customers')} to the target`)
      } else if (total > 0) {
        lines.push(`Stays: ${f.stays}`)
      }
    }
    if (Number(c.active_site_links || 0) > 0) lines.push('Removes the events from the source booking site')
    lines.push('Archives the source location')
  }
  for (const l of createOnlyLines.value) lines.push(l)
  return lines
})

const canSubmit = computed(() => {
  if (props.loading) return false
  if (mode.value === 'new') {
    return !errors.value.name && !errors.value.code && name.value.trim().length > 0 && code.value.trim().length > 0
  }
  if (!props.preview || hasBlocking.value || !targetGroupId.value) return false
  if (needsConfirmation.value) {
    if (!acknowledgments.value.every((_, i) => acks.value[i] === true)) return false
    if (confirmName.value.trim() !== targetName.value.trim()) return false
  }
  return true
})

const submitLabel = computed(() => {
  if (props.loading) return mode.value === 'move' ? 'Merging...' : 'Copying...'
  if (mode.value === 'copy_existing') return 'Copy settings'
  if (mode.value === 'move') return 'Merge location'
  return 'Copy'
})

function matchValue(map: string, sourceId: number): number {
  const m = matches.value[map]
  const v = m ? m[String(sourceId)] : null
  return v === null || v === undefined ? 0 : v
}

function setMatch(map: string, sourceId: number, value: number) {
  const next = { ...(matches.value[map] || {}) }
  next[String(sourceId)] = value === 0 ? null : value
  matches.value = { ...matches.value, [map]: next }
}

function seedMatchesFromPreview() {
  const out: Record<string, Record<string, number | null>> = {}
  for (const m of (props.preview && props.preview.matchables) || []) {
    out[m.map] = { ...(m.proposed || {}) }
  }
  matches.value = out
}

function resetPreview() {
  acks.value = acknowledgments.value.map(() => false)
  confirmName.value = ''
  matches.value = {}
  emit('preview-reset')
}

function onModeChange(v: string) {
  mode.value = v as Mode
  if (mode.value === 'copy_existing') selectedAreas.value = AREAS.filter((a) => !a.replace).map((a) => a.key)
  else if (mode.value === 'new') selectedAreas.value = AREAS.map((a) => a.key)
  resetPreview()
}

function onTargetChange(v: number) {
  targetGroupId.value = v
  resetPreview()
}

function onToggleAll(e: Event) {
  const checked = (e.target as HTMLInputElement).checked
  selectedAreas.value = checked ? AREAS.map((a) => a.key) : []
  resetPreview()
}

function areasPayload(): Record<string, boolean> {
  const areas: Record<string, boolean> = {}
  for (const a of AREAS) areas[a.key] = selectedAreas.value.includes(a.key)
  return areas
}

// Only matches whose source row is in the preview are sent, as { map: { sourceId: targetId } }
// with null for "create copy".
function matchesPayload(): Record<string, Record<string, number | null>> {
  const out: Record<string, Record<string, number | null>> = {}
  for (const [map, pairs] of Object.entries(matches.value)) {
    const kept: Record<string, number | null> = {}
    for (const [k, v] of Object.entries(pairs)) if (v !== null && v !== undefined) kept[k] = v
    if (Object.keys(kept).length > 0) out[map] = kept
  }
  return out
}

function basePayload(): Record<string, unknown> {
  const p: Record<string, unknown> = { mode: mode.value, target_group_id: targetGroupId.value }
  if (mode.value === 'copy_existing') p.areas = areasPayload()
  if (mode.value === 'move') {
    const families: Record<string, boolean> = {}
    for (const f of FAMILIES) families[f.key] = selectedFamilies.value.includes(f.key)
    p.families = families
  }
  return p
}

function onPreview() {
  if (!canPreview.value) return
  acks.value = acknowledgments.value.map(() => false)
  confirmName.value = ''
  emit('preview', basePayload())
}

function onClose() {
  emit('close')
}

function onSubmit() {
  if (mode.value === 'new') {
    touched.value = { name: true, code: true }
    const nameOk = validateName()
    const codeOk = validateCode()
    if (!nameOk || !codeOk) return
    emit('copy', { name: name.value.trim(), code: code.value.trim(), areas: areasPayload() })
    return
  }
  if (!canSubmit.value) return
  const payload = basePayload()
  payload.matches = matchesPayload()
  if (needsConfirmation.value) {
    payload.acknowledged = true
    payload.confirm_name = confirmName.value.trim()
  }
  emit('run', payload)
}

function onEscKey(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.show) onClose()
}

// Validate as the user types (after the first keystroke in each field) and on submit.
watch(name, () => { if (touched.value.name) validateName() })
watch(code, () => { if (touched.value.code) validateCode() })

// A fresh preview seeds the match table and clears the confirmation state.
watch(() => props.preview, (p) => {
  if (p) {
    seedMatchesFromPreview()
    acks.value = acknowledgments.value.map(() => false)
    confirmName.value = ''
  }
})

watch(
  () => props.show,
  (visible) => {
    if (visible) {
      mode.value = 'new'
      name.value = `${sourceName.value} copy`.trim().slice(0, 50)
      code.value = ''
      errors.value = { name: '', code: '' }
      touched.value = { name: false, code: false }
      selectedAreas.value = AREAS.map((a) => a.key)
      targetGroupId.value = null
      selectedFamilies.value = FAMILIES.map((f) => f.key)
      matches.value = {}
      acks.value = []
      confirmName.value = ''
      emit('preview-reset')
      document.addEventListener('keydown', onEscKey)
    } else {
      document.removeEventListener('keydown', onEscKey)
    }
  }
)

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onEscKey)
})
</script>

<style scoped lang="scss">
/* Same checkbox rows as the webhook drawer (WebhooksSettings.vue .event-row): native
   checkbox with the brand accent, white label, neutral hover highlight only. */
.copy-group-rows { display: flex; flex-direction: column; }
.copy-group-row {
  display: flex; align-items: center; gap: 0.6rem;
  padding: 0.5rem 0.55rem; border-radius: 0.35rem; cursor: pointer; margin: 0;
  transition: background 0.12s ease;
  &:hover { background: rgba(125, 125, 125, 0.08); }
  input { accent-color: #e14eca; cursor: pointer; width: 15px; height: 15px; flex-shrink: 0; }
}
.copy-group-row--all {
  margin-bottom: 0.4rem;
  padding-bottom: 0.9rem;
  border-bottom: 1px solid rgba(125, 125, 125, 0.25);
  border-radius: 0;
}
.copy-group-row--ack { align-items: flex-start; input { margin-top: 3px; } }
.copy-group-row--match { cursor: default; }
.copy-group-name { font-size: 0.85rem; color: #fff; flex: 1; }
.copy-group-match-select { width: 240px; max-width: 55%; flex-shrink: 0; }

.footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

/* Light mode parity (.white-content is toggled on an ancestor) */
.white-content .copy-group-name { color: #1d253b; }
.white-content .copy-group-row:hover { background: rgba(0, 0, 0, 0.04); }
</style>
